import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import registerApi, { getRegisterToken, clearRegisterToken } from '../api/registerAxios';
import { T, Alert } from '../components/UI';
import StaffDetailsFields, { EMPTY_DETAILS as EMPTY, validateDetails, dateOnly } from '../components/StaffDetailsFields';
import logo from '../assets/logo.js';

const Screen = ({ icon, color, bg, title, children, action }) => (
  <div style={{ textAlign:'center', padding:'40px 20px' }}>
    <div style={{ width:'60px', height:'60px', borderRadius:'50%', background:bg, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
      <i className={`ti ${icon}`} style={{ fontSize:'28px', color }} aria-hidden="true"/>
    </div>
    <h2 style={{ fontSize:'18px', fontWeight:'600', color:'var(--text)', margin:'0 0 8px' }}>{title}</h2>
    <p style={{ fontSize:'14px', color:'var(--muted)', lineHeight:1.6, maxWidth:'420px', margin:'0 auto' }}>{children}</p>
    {action}
  </div>
);

// Public page: a staff member fills in their own details and uploads their driver licence
export default function StaffRegisterForm() {
  const navigate = useNavigate();

  const [reg,     setReg]     = useState(null);
  const [form,    setForm]    = useState(EMPTY);
  const [loadErr, setLoadErr] = useState(null);
  const [busy,    setBusy]    = useState(null);   // 'save' | 'submit'
  const [error,   setError]   = useState(null);
  const [notice,  setNotice]  = useState(null);
  const [saved,   setSaved]   = useState(false);
  const [dirty,   setDirty]   = useState(false);   // edits not yet saved to the server
  const [errors,  setErrors]  = useState({});
  const [accessUntil, setAccessUntil] = useState(null);   // when the emailed code stops working
  const [done,    setDone]    = useState(false);          // submitted: the code is used up and the form is locked

  // Already submitted (waiting for review, or approved) — the form stays editable, saved as "changes"
  const submitted = reg?.status === 'pending' || reg?.status === 'approved';

  const signOut = useCallback((notice) => { clearRegisterToken(); navigate('/staff-register', { replace:true, state: typeof notice === 'string' ? { notice } : undefined }); }, [navigate]);

  const applyRegistration = useCallback((r, { keepForm } = {}) => {
    setReg(r);
    if (keepForm) return;
    const loaded = { ...EMPTY };
    Object.keys(EMPTY).forEach(k => { if (r[k] !== undefined && r[k] !== null) loaded[k] = r[k]; });
    loaded.dateOfBirth   = dateOnly(r.dateOfBirth);
    loaded.licenceExpiry = dateOnly(r.licenceExpiry);
    setForm(loaded);
  }, []);

  useEffect(() => {
    if (!getRegisterToken()) { navigate('/staff-register', { replace:true }); return; }
    registerApi.get('/me')
      .then(({ data }) => { applyRegistration(data.registration); setAccessUntil(data.accessExpiresAt); })
      .catch(err => {
        if (err.response?.status === 401) { clearRegisterToken(); navigate('/staff-register', { replace:true, state:{ notice: err.response?.data?.message } }); return; }
        setLoadErr(err.response?.data?.message || 'Something went wrong. Please try again.');
      });
  }, [navigate, applyRegistration]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: false })); setSaved(false); setDirty(true); setNotice(null); };

  const validate = () => {
    const errs = validateDetails(form, { front: !!reg?.licenceFront?.uploaded, back: !!reg?.licenceBack?.uploaded });
    setErrors(errs);
    return errs;
  };

  const save = async () => {
    const { data } = await registerApi.patch('/me', form);
    applyRegistration(data.registration, { keepForm:true });
    setDirty(false);
  };

  // Browsers show their own "leave site?" prompt — stops a refresh or back-swipe losing typing
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const handleSave = async () => {
    setBusy('save'); setError(null);
    try { await save(); setSaved(true); }
    catch (err) { setError(err.response?.data?.message || 'Could not save. Please try again.'); }
    finally { setBusy(null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setError('Please fix the highlighted fields.');
      window.scrollTo({ top:0, behavior:'smooth' });
      return;
    }
    if (!window.confirm('Submit now? Your code stops working as soon as you submit, so please check your details first.')) return;
    setBusy('submit'); setError(null); setNotice(null);
    try {
      await save();
      await registerApi.post('/me/submit');
      setDirty(false);
      clearRegisterToken();   // the code is used up — nothing more can be done with this session
      setDone(true);
      window.scrollTo({ top:0 });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not submit. Please try again.');
      window.scrollTo({ top:0, behavior:'smooth' });
    } finally { setBusy(null); }
  };

  // ── Licence upload handlers ────────────────────────────
  const fetchLicence = (side) => () => registerApi.get(`/me/licence/${side}`, { responseType:'blob' }).then(r => r.data);
  const uploadLicence = (side, key) => async (file) => {
    const body = new FormData();
    body.append('file', file);
    const { data } = await registerApi.put(`/me/licence/${side}`, body, { headers:{ 'Content-Type':'multipart/form-data' } });
    // Merge only this side, so two uploads finishing together don't overwrite each other
    setReg(r => ({ ...r, [key]: data.registration[key] }));
    setErrors(e => ({ ...e, [key]: false }));
  };
  const removeLicence = (side, key) => async () => {
    const { data } = await registerApi.delete(`/me/licence/${side}`);
    setReg(r => ({ ...r, [key]: data.registration[key] }));
  };

  const header = (
    <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'10px 16px', display:'flex', alignItems:'center', gap:'10px', position:'sticky', top:0, zIndex:100 }}>
      <div style={{ width:'34px', height:'34px', background:'#000', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <img src={logo} alt="" style={{ width:'30px', height:'30px', objectFit:'contain' }}/>
      </div>
      <div style={{ flex:1, fontSize:'15px', fontWeight:'600', color:'var(--text)' }}>Staff Registration</div>
      <button onClick={() => signOut()} style={{ ...T.btnSecondary, padding:'6px 12px', fontSize:'12px' }}>Close</button>
    </div>
  );

  const shell = (children) => (
    <div style={{ ...T.page, minHeight:'100vh' }}>{header}{children}</div>
  );

  if (done) return shell(
    <Screen icon="ti-circle-check" color="var(--green)" bg="var(--green-bg)" title="Thanks — your details are submitted">
      A manager will review your registration. Your code has now been used, so the form is locked. If you need to change anything later, ask your manager and they’ll send you a new code.
    </Screen>);

  if (loadErr) return shell(<Screen icon="ti-alert-circle" color="var(--red)" bg="var(--red-bg)" title="Something went wrong"
    action={<div style={{ marginTop:'20px' }}><button onClick={()=>window.location.reload()} style={T.btnPrimary}>Try again</button></div>}>{loadErr}</Screen>);

  if (!reg) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
    </div>
  );

  if (reg.status === 'approving') return shell(
    <Screen icon="ti-clock-check" color="var(--amber)" bg="var(--amber-bg)" title="A manager is approving your registration">
      This only takes a moment. Please check back in a minute.
    </Screen>);

  return shell(
    <form onSubmit={handleSubmit} noValidate style={{ maxWidth:'700px', margin:'0 auto', padding:'20px 16px 130px' }}>
      {reg.status === 'rejected' && reg.rejectionNote && (
        <Alert type="error"><strong>Your manager sent this back:</strong> {reg.rejectionNote}</Alert>
      )}
      {reg.status === 'pending' && (
        <Alert type="warning">You’ve already submitted this registration and a manager is reviewing it. Change anything that needs fixing, then press <strong>Submit changes</strong>.</Alert>
      )}
      {reg.status === 'approved' && (
        <Alert type="success">
          You’re registered. If anything needs updating, change it below and press <strong>Submit changes</strong>; your manager will be told.
        </Alert>
      )}
      {notice && <Alert type="success">{notice}</Alert>}
      {error && <Alert type="error">{error}</Alert>}

      <p style={{ fontSize:'13px', color:'var(--muted)', lineHeight:1.6, marginBottom:'16px' }}>
        Please fill in your details below. Fields marked * are required. You can save and come back with the same code
        {accessUntil ? <> until <strong>{new Date(accessUntil).toLocaleString('en-AU', { timeZone:'Australia/Perth', weekday:'short', day:'numeric', month:'short', hour:'numeric', minute:'2-digit', hour12:true })}</strong></> : ''}.
        Once you press Submit, the code stops working and the form is locked.
      </p>

      <StaffDetailsFields form={form} errors={errors} set={set} signatureLabel="Your signature *"
        licenceNote="Your licence is stored privately and can only be seen by Car Saloon management."
        licence={{
          front: { meta: reg.licenceFront, fetchBlob: fetchLicence('front'), onUpload: uploadLicence('front', 'licenceFront'), onRemove: submitted ? undefined : removeLicence('front', 'licenceFront') },
          back:  { meta: reg.licenceBack,  fetchBlob: fetchLicence('back'),  onUpload: uploadLicence('back', 'licenceBack'),   onRemove: submitted ? undefined : removeLicence('back', 'licenceBack') },
        }}/>

      <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'var(--surface)', borderTop:'1px solid var(--border)', padding:'12px 16px', paddingBottom:'calc(12px + env(safe-area-inset-bottom, 0px))', display:'flex', justifyContent:'flex-end', alignItems:'center', gap:'8px', zIndex:150 }}>
        {saved && <span style={{ fontSize:'12px', color:'var(--green)', marginRight:'auto' }}><i className="ti ti-check" aria-hidden="true"/> Saved</span>}
        <button type="button" onClick={handleSave} disabled={!!busy} style={{ ...T.btnSecondary, opacity:busy?0.6:1 }}>
          {busy === 'save' ? 'Saving...' : submitted ? 'Save progress' : 'Save draft'}
        </button>
        <button type="submit" disabled={!!busy} style={{ ...T.btnPrimary, opacity:busy?0.6:1 }}>
          <i className="ti ti-send" aria-hidden="true"/>{' '}
          {busy === 'submit' ? 'Submitting...' : submitted ? 'Submit changes' : 'Submit'}
        </button>
      </div>
    </form>
  );
}
